/**
 * Report blocks — layer 3 of 4.
 *
 * The renderer receives an ordered array of these. It switches on `kind` to
 * pick a component and on `tone` to pick styling, and that is the whole of its
 * intelligence — it asks no questions about the plan.
 *
 * This file owns the document's shape: which sections exist for a given plan,
 * what number each one gets, how a cross-reference resolves, which findings are
 * promoted to the summary, and what happens when a plan trips more warnings
 * than a page can carry.
 */

import type { PlanFacts } from "./derive";
import { THRESHOLDS } from "./derive";
import { capitalise, words } from "./format";

/* ─────────────────────────────── blocks ─────────────────────────────── */

export type BlockKind = "lead" | "callout" | "table-note" | "list" | "table";
export type Tone = "good" | "neutral" | "caution" | "critical";

/** A cross-reference that resolves to "Section 07", or drops if absent. */
export interface SectionRef {
  ref: SectionKey;
}

/** Body text is fragments so a trailing clause can vanish on its own. */
export type Fragment = string | SectionRef;

export interface Block {
  /**
   * The RULE id, stable and shared across subjects — "bond.maturesAfterGoal".
   * Used for QA, analytics, suppression, and summary de-duplication, so two
   * goals tripping the same rule collapse to one headline.
   */
  id: string;
  section: SectionKey;
  kind: BlockKind;
  tone: Tone;
  title?: string;
  body: Fragment[][];
  subject?: "plan" | "goal" | "liability" | "swp";
  subjectId?: number | string;
  subjectName?: string;
  /** Eligible for the at-a-glance roll-up. */
  summary?: boolean;
  /**
   * Framing rather than warning — the risk-vs-horizon box, the category-fit
   * box. Exempt from the callout budget: cutting these to make room for a
   * fourth warning makes the page worse.
   */
  explanatory?: boolean;
  /** Set by the budget pass. Demoted blocks move to the overflow appendix. */
  demoted?: boolean;
  /** Higher wins when the budget has to choose between equal tones. */
  weight?: number;
}

/* ────────────────────────────── sections ────────────────────────────── */

export type SectionKey =
  | "cover"
  | "glance"
  | "goals-overview"
  | "goal"
  | "deployment"
  | "sweep"
  | "gap"
  | "goal-funds"
  | "liability"
  | "liability-funds"
  | "retirement"
  | "what-it-takes"
  | "monthly"
  | "orders"
  | "tax"
  | "appendix-trace"
  | "appendix-cashflows"
  | "appendix-overflow"
  | "appendix-assumptions"
  | "appendix-revision";

export interface Section {
  key: SectionKey;
  /**
   * Computed, never written into copy. "01".."15" for numbered sections,
   * "A"/"B" for appendices, "" for the cover — which carries no number.
   */
  number: string;
  title: string;
  /**
   * The running group a section belongs to — "PART ONE". Goals, loans and the
   * drawdown each open a part; the plan-level sections belong to none.
   */
  part?: string;
  /** What that part is about — "Retirement". Repeated on every section in it. */
  partName?: string;
  /** Present on the repeating sections. */
  subjectId?: number | string;
  subjectName?: string;
}

/** Part labels in encounter order. Beyond eight parts it falls back to digits. */
const PART_WORDS = [
  "ONE",
  "TWO",
  "THREE",
  "FOUR",
  "FIVE",
  "SIX",
  "SEVEN",
  "EIGHT",
] as const;

const partLabel = (index: number): string =>
  `PART ${PART_WORDS[index] ?? String(index + 1)}`;

/** Sections that carry a letter instead of a number. */
const APPENDIX_KEYS = new Set<SectionKey>([
  "appendix-trace",
  "appendix-cashflows",
  "appendix-overflow",
  "appendix-assumptions",
  "appendix-revision",
]);

const TITLES: Record<SectionKey, string> = {
  cover: "Cover",
  glance: "Your Plan at a Glance",
  "goals-overview": "Your Goals",
  goal: "Goal",
  deployment: "Lumpsum Deployment",
  sweep: "Coupon Sweep",
  gap: "The Gap, and the SIP That Closes It",
  "goal-funds": "Where the Money Goes",
  liability: "Loan Interest Cancel-Out",
  "liability-funds": "Where the Offset Is Invested",
  retirement: "Retirement Income",
  "what-it-takes": "What It Would Take",
  monthly: "The Monthly Picture",
  orders: "Your Orders",
  tax: "How This Plan Is Taxed",
  "appendix-trace": "Calculation Traces",
  "appendix-cashflows": "Bond Cash Flows",
  "appendix-overflow": "Further Findings",
  "appendix-assumptions": "Assumptions",
  "appendix-revision": "Revisions",
};

/**
 * A goal or loan titles its own section. "The Family Trip" reads correctly on
 * its own; "The Retirement" does not, so a single-word name takes the noun back
 * — which is the difference between the two goal titles in the sample report.
 */
const subjectTitle = (name: string, noun: "Goal" | "Loan"): string =>
  name.trim().includes(" ") ? `The ${name}` : `The ${name} ${noun}`;

/**
 * Decides which sections exist before a single sentence is chosen. Everything
 * downstream depends on this list, including the numbering.
 *
 * Two are conditional on a condition rather than on presence: "what it would
 * take" needs the drawdown to exist AND to be exhausted — printing an empty
 * page when the corpus sustains itself reads as a bug. Same for the revision
 * appendix, which exists only when a correction actually fired.
 */
export const buildSkeleton = (
  facts: PlanFacts,
  options: { corrections?: number; overflow?: boolean } = {}
): Section[] => {
  type Entry = {
    key: SectionKey;
    part?: string;
    partName?: string;
    subjectId?: number | string;
    subjectName?: string;
  };
  const keys: Entry[] = [];
  let parts = 0;

  keys.push({ key: "cover" });
  keys.push({ key: "glance" });
  if (facts.goalCount > 1) keys.push({ key: "goals-overview" });

  facts.goals.forEach((goal) => {
    const part = partLabel(parts);
    parts += 1;
    const subject = {
      part,
      partName: goal.label,
      subjectId: goal.id,
      subjectName: goal.label,
    };
    keys.push({ key: "goal", ...subject });
    /*
     * A goal funded by SIP alone has one page of material: the target, the
     * instalment that reaches it, and the basket it buys. Numbering that as
     * three sections pads the contents page and makes the document look longer
     * than it is, so the renderer draws the gap and the basket inside the goal
     * section and only the bond route earns sections of its own — where there
     * genuinely is a page each of pricing, sweep and selection to work through.
     */
    if (goal.bond) {
      keys.push({ key: "deployment", ...subject });
      keys.push({ key: "sweep", ...subject });
      if (goal.sip > 0) keys.push({ key: "gap", ...subject });
      if (goal.funds.count > 0) keys.push({ key: "goal-funds", ...subject });
    }
  });

  facts.liabilities.forEach((liability, index) => {
    const part = partLabel(parts);
    parts += 1;
    const subject = {
      part,
      partName: liability.label,
      subjectId: index,
      subjectName: liability.label,
    };
    keys.push({ key: "liability", ...subject });
    if (liability.funds.count > 0) keys.push({ key: "liability-funds", ...subject });
  });

  if (facts.swp) {
    const part = partLabel(parts);
    parts += 1;
    const subject = { part, partName: "Retirement Income" };
    keys.push({ key: "retirement", ...subject });
    if (facts.swp.exhausted) keys.push({ key: "what-it-takes", ...subject });
  }

  keys.push({ key: "monthly" });
  if (facts.orderCount > 0) keys.push({ key: "orders" });
  keys.push({ key: "tax" });
  keys.push({ key: "appendix-trace" });
  /*
   * The assumptions table is half a page. On a bond plan it rides with the
   * coupon schedule under one heading rather than opening an appendix of its
   * own for six rows; with no bond there is no schedule to ride with.
   */
  const hasBond = facts.goals.some((goal) => goal.bond);
  if (hasBond) keys.push({ key: "appendix-cashflows" });
  if (options.overflow) keys.push({ key: "appendix-overflow" });
  if (!hasBond) keys.push({ key: "appendix-assumptions" });
  if (options.corrections) keys.push({ key: "appendix-revision" });

  /*
   * Numbering runs over the body only. The cover carries no number and the
   * appendices carry letters, so dropping the loan and the drawdown renumbers
   * every later body section — which is why no sentence may contain a literal
   * "Section 08".
   */
  let bodyNumber = 0;
  let appendixLetter = 0;

  return keys.map((entry) => {
    let number = "";
    if (entry.key === "cover") {
      number = "";
    } else if (APPENDIX_KEYS.has(entry.key)) {
      number = String.fromCharCode(65 + appendixLetter);
      appendixLetter += 1;
    } else {
      bodyNumber += 1;
      number = String(bodyNumber).padStart(2, "0");
    }

    let title = TITLES[entry.key];
    if (entry.subjectName && entry.key === "goal") {
      title = subjectTitle(entry.subjectName, "Goal");
    } else if (entry.subjectName && entry.key === "liability") {
      title = subjectTitle(entry.subjectName, "Loan");
    } else if (entry.subjectName && entry.key === "goal-funds") {
      title = `Where the ${entry.subjectName} SIP Goes`;
    } else if (entry.key === "appendix-cashflows") {
      title = "Bond Cash Flows and Assumptions";
    } else if (entry.key === "goals-overview") {
      // "Your Two Goals" — the count is the first thing the page establishes.
      title = `Your ${capitalise(words(facts.goalCount))} Goals`;
    }

    return {
      key: entry.key,
      number,
      title,
      part: entry.part,
      partName: entry.partName,
      subjectId: entry.subjectId,
      subjectName: entry.subjectName,
    };
  });
};

/* ────────────────────────── cross-references ────────────────────────── */

const isRef = (fragment: Fragment): fragment is SectionRef =>
  typeof fragment === "object" && fragment !== null && "ref" in fragment;

/**
 * Resolves {ref: "liability"} to "Section 07" in one plan and "Section 05" in
 * another — or drops the clause entirely when that section does not exist in
 * this plan. A reference whose target is missing must remove the clause, never
 * print a broken link.
 */
export const resolveBody = (
  body: Fragment[][],
  sections: Section[]
): string[] => {
  const numberOf = (key: SectionKey): string | null =>
    sections.find((section) => section.key === key)?.number ?? null;

  return body
    .map((fragments) => {
      let dropped = false;
      const text = fragments
        .map((fragment) => {
          if (!isRef(fragment)) return fragment;
          const number = numberOf(fragment.ref);
          if (!number) {
            dropped = true;
            return "";
          }
          // Appendices carry a letter, and "Section A" reads as a typo.
          return /^[A-Z]$/.test(number) ? `Appendix ${number}` : `Section ${number}`;
        })
        .join("");
      // "As flagged in Section 07, a ₹70,00,000 balance…" has to become
      // "A ₹70,00,000 balance…" rather than losing the sentence.
      const cleaned = dropped
        ? text
            .replace(/\s*,\s*,/g, ",")
            .replace(/^\s*(as flagged in|as set out in|see)\s*,?\s*/i, "")
            .replace(/\s{2,}/g, " ")
            .trim()
        : text.trim();
      return cleaned ? cleaned[0].toUpperCase() + cleaned.slice(1) : "";
    })
    .filter(Boolean);
};

/* ──────────────────────── budget and roll-up ────────────────────────── */

const TONE_ORDER: Record<Tone, number> = {
  critical: 0,
  caution: 1,
  neutral: 2,
  good: 3,
};

const byTone = (a: Block, b: Block): number =>
  TONE_ORDER[a.tone] - TONE_ORDER[b.tone] || (b.weight ?? 0) - (a.weight ?? 0);

/**
 * A plan can trip fifteen conditions. Rendered as fifteen boxes the reader
 * stops distinguishing them and the critical one is lost among the
 * informational ones.
 *
 * Boxed callouts are capped per section per subject. When cutting, the most
 * severe stays and explanatory blocks are exempt. Nothing is silently dropped —
 * a demoted block is flagged and moves to the overflow appendix with a pointer.
 */
export const applyCalloutBudget = (
  blocks: Block[],
  budget = THRESHOLDS.calloutBudget
): { blocks: Block[]; demoted: Block[] } => {
  const buckets = new Map<string, Block[]>();
  blocks.forEach((block) => {
    if (block.kind !== "callout" || block.explanatory) return;
    const key = `${block.section}::${block.subjectId ?? "plan"}`;
    const bucket = buckets.get(key) ?? [];
    bucket.push(block);
    buckets.set(key, bucket);
  });

  const demoted: Block[] = [];
  buckets.forEach((bucket) => {
    if (bucket.length <= budget) return;
    bucket
      .slice()
      .sort(byTone)
      .slice(budget)
      .forEach((block) => {
        block.demoted = true;
        demoted.push(block);
      });
  });

  return { blocks: blocks.filter((block) => !block.demoted), demoted };
};

/**
 * The at-a-glance page is the one most likely to contradict the rest of the
 * document, because it is the one a human is most tempted to hand-write. It is
 * built from the blocks that already fired instead.
 *
 * De-duplication matters: a goal fund and a loan fund both at 100% of one house
 * trip the concentration rule twice and would otherwise fill half the summary
 * with the same sentence.
 */
export const rollUp = (blocks: Block[], limit = 4): Block[] => {
  const seen = new Set<string>();
  return blocks
    .filter(
      (block) => block.summary && !block.demoted && block.section !== "glance"
    )
    .sort(byTone)
    .filter((block) => {
      if (seen.has(block.id)) return false;
      seen.add(block.id);
      return true;
    })
    .slice(0, limit);
};

/**
 * A plan with no problems still needs a summary page. If nothing critical or
 * caution-level fired, the roll-up presents the good findings instead — an
 * empty box is worse than a positive one, and inventing a warning to fill it is
 * worse than both.
 */
export const summaryIsPositive = (surfaced: Block[]): boolean =>
  surfaced.length > 0 &&
  surfaced.every((block) => block.tone === "good" || block.tone === "neutral");
